/**
 * The General Knowledge "Reference files" shelf: uploads indexed as-is and
 * cited in chat by filename. A row opens the file's page, where the equipment
 * it applies to is set; untagged files apply to every question.
 */
import { Link } from 'react-router-dom';
import { ChevronRight, FileText } from '@servicecompressionllc/sc-design-system/icons';
import type { KnowledgeDocument } from './api.js';
import { humanSize } from '../../lib/format.js';

export const ReferenceFiles = ({
  rows,
  bucketId,
}: {
  rows: KnowledgeDocument[];
  /** The bucket these rows are shown under (the default bucket's id for unstamped files). */
  bucketId: string;
}) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
      Reference files
    </span>
    {rows.length === 0 ? (
      <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-ink-subtle">
        No reference files yet. Files added here are searchable on every question and cited by
        filename. Set the equipment a manual covers on its page so it’s only used for that
        equipment.
      </div>
    ) : (
      <div className="rounded-lg border border-outline bg-surface-elevated px-4">
        <ul className="divide-y divide-outline">
          {rows.map((d) => (
            <li key={d.id}>
              <Link
                to={`/knowledge/buckets/${bucketId}/documents/${d.id}`}
                className="flex items-center gap-3 py-2.5 hover:bg-surface-hover"
              >
                <FileText size={16} aria-hidden className="shrink-0 text-ink-subtle" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink" title={d.filename}>
                    {d.filename}
                  </span>
                  <span className="block text-xs text-ink-subtle">
                    {humanSize(d.sizeBytes)} · {d.uploadedByEmail} · cited in chat by filename
                  </span>
                </span>
                <span className="w-56 shrink-0 truncate text-sm text-ink-muted">
                  {d.subjects.length === 0
                    ? 'Everywhere'
                    : d.subjects
                        .map((s) => (s.archived ? `${s.name} (deleted)` : s.name))
                        .join(', ')}
                </span>
                <ChevronRight size={16} aria-hidden className="shrink-0 text-ink-subtle" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
